import React from "react";
import { motion } from "framer-motion";

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            className="text-4xl md:text-5xl font-inter font-bold text-acBlack leading-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Refund Policy
          </motion.h1>
          <motion.p 
            className="text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Last updated: 19 September 2025
          </motion.p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="prose prose-gray max-w-none"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-gray-700 leading-relaxed">
                This Refund Policy explains when and how Validate Systems ("Validate Systems", "we", "us") issues refunds for payments made on our website or invoices processed via Flutterwave. It should be read together with our Terms of Service and Privacy Policy.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                If anything here conflicts with mandatory consumer or payments regulation, the applicable law/regulation will prevail.
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">1. What our services are (context)</h2>
              <div className="space-y-4 text-gray-700">
                <p>We provide (i) Verification services (human-verified checks) and (ii) Travel & Immigration administrative support. We do not guarantee outcomes or processing timelines by third parties (e.g., registrars, visa posts). See our Terms for full scope.</p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">2. General refund principles</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>Work already performed is non-refundable.</strong> Fees for time spent on advisory, outreach, fieldwork, coordination, admin QA, scheduling attempts, and partially completed checks are not refundable. (Terms §8.1)</p>
                <p><strong>Undeliverable line items.</strong> If a specific, paid line item is objectively undeliverable despite best efforts (e.g., an institution is lawfully inaccessible), we will either (a) propose a suitable alternative of equal value, or (b) refund that undeliverable line item. (Terms §8.2)</p>
                <p><strong>Client cancellations.</strong> If you cancel after acceptance, we will deduct work completed and committed third-party costs; any remainder for undelivered line items may be returned at our discretion in line with §8.2. (Terms §8.3)</p>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">3. Payments processed via Flutterwave</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>Where refunds go.</strong> Refunds are sent back to the original source account or card used for the payment (you may be asked to confirm the funding account before processing).</p>
                <p><strong>How we issue refunds.</strong> We initiate refunds from our Flutterwave dashboard/API referencing your original transaction; we can process partial refunds for eligible transactions.</p>
              </div>
            </div>

            {/* Section 4 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">4. Refund timelines (by payment channel)</h2>
              <div className="space-y-4 text-gray-700">
                <p>Actual timelines depend on your bank/card scheme and payment channel. After we approve a refund:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Card payments:</strong> typically 3–15 business days for the card issuer to post the refund.</li>
                  <li><strong>Account/bank transfer payments:</strong> typically 24–48 business hours after processing.</li>
                  <li><strong>Failed/duplicate transactions:</strong> If a payment fails or is duplicated (you were charged but service was not provided), we will validate and trigger a refund promptly. In Nigeria, CBN guidance expects refunds on failed POS/WEB transactions within 48 hours and "on-us" ATM reversals to be instant (24–48h if manual). We follow these standards where applicable and supported by Flutterwave/banks.</li>
                </ul>
                <p className="mt-4">We will email you once a refund is initiated. Your bank's posting time may vary.</p>
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">5. Non-refundable items & costs</h2>
              <div className="space-y-4 text-gray-700">
                <p>The following are not refundable once incurred or delivered, unless a line item is objectively undeliverable (§2):</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Time & effort already performed (advisory, outreach, field visits, admin QA, mock interviews).</li>
                  <li>Third-party/vendor costs (e.g., translation, notarization, courier, government/registry fees, visa center fees).</li>
                  <li>Rush/after-hours surcharges once work has started.</li>
                </ul>
              </div>
            </div>

            {/* Section 6 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">6. Partial refunds & alternatives</h2>
              <div className="space-y-4 text-gray-700">
                <p>We may issue partial refunds for the undeliverable portion of a multi-line order, or offer a like-for-like alternative (e.g., substitute verification method or equivalent jurisdictional check) of equal value, with your approval.</p>
                <p>Partial refunds are processed via Flutterwave to the source account.</p>
              </div>
            </div>

            {/* Section 7 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">7. Chargebacks & our good-faith process</h2>
              <div className="space-y-4 text-gray-700">
                <p>If you believe a charge is wrong, please notify us first at <a href="mailto:legal@validatesystems.com" className="text-acYellow hover:text-green-700 transition-colors">legal@validatesystems.com</a> so we can investigate and, where appropriate, refund directly.</p>
                <p>Chargebacks are initiated through your bank and may incur scheme/bank fees and evidence processes. Merchants (and Flutterwave) may hold rolling reserves to cover chargebacks/refunds per the merchant agreement.</p>
                <p>Per our Terms, you agree not to file chargebacks for services already rendered; we will dispute unfounded chargebacks with evidence of delivery and effort.</p>
              </div>
            </div>

            {/* Section 8 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">8. Payment Protection programs</h2>
              <div className="space-y-4 text-gray-700">
                <p>Flutterwave operates consumer-protection initiatives for certain scenarios and values (e.g., the Payment Protection Promise). Where applicable to your transaction type, we will cooperate fully. Note that coverage criteria and limits are set by Flutterwave.</p>
              </div>
            </div>

            {/* Section 9 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">9. How to request a refund from us</h2>
              <div className="space-y-4 text-gray-700">
                <p>Email <a href="mailto:legal@validatesystems.com" className="text-acYellow hover:text-green-700 transition-colors">legal@validatesystems.com</a> (cc <a href="mailto:info@validatesystems.com" className="text-acYellow hover:text-green-700 transition-colors">info@validatesystems.com</a>) with:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your full name/organization and contact email;</li>
                  <li>The Flutterwave transaction reference, invoice number, and payment date;</li>
                  <li>The service purchased and the reason for refund;</li>
                  <li>Any supporting documents (e.g., duplicate charge proof, evidence that a specific line item is undeliverable).</li>
                </ul>
                <p className="mt-4">We aim to acknowledge within 1 business day (Mon–Fri, 09:00–17:00 WAT) and confirm next steps.</p>
              </div>
            </div>

            {/* Section 10 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">10. Currency, fees & FX</h2>
              <div className="space-y-4 text-gray-700">
                <p>Refunds are processed in the original payment currency and sent back to the source account.</p>
                <p>We do not refund bank/card FX spreads, cross-border fees, or remittance fees charged by your provider. If a currency conversion occurred, your bank/card issuer's rate at refund posting will apply.</p>
                <p>Where regulations require a different approach, we will follow the regulation.</p>
              </div>
            </div>

            {/* Section 11 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">11. Abuse & fraud</h2>
              <div className="space-y-4 text-gray-700">
                <p>We may refuse or reverse refunds where there is evidence of fraud, misuse, or policy abuse, and we may report suspicious activity to relevant parties as allowed by law and the Flutterwave Merchant Agreement.</p>
              </div>
            </div>

            {/* Section 12 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">12. Updates</h2>
              <div className="space-y-4 text-gray-700">
                <p>We may update this policy to reflect changes in law, card-scheme/bank rules, or Flutterwave processes. The "Last updated" date will change accordingly.</p>
              </div>
            </div>

            {/* Contact Section */}
            <div className="mb-10 bg-gray-50 rounded-lg p-8">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">Contact</h2>
              <p className="text-gray-700 mb-4">Questions about this Refund Policy?</p>
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:legal@validatesystems.com" className="text-acYellow hover:text-green-700 transition-colors">legal@validatesystems.com</a>
              </p>
            </div>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicy;
